import { getOwner } from '@ember/application';
import Evented from '@ember/object/evented';
import Service, { inject as service } from '@ember/service';
import { assert } from '@ember/debug';
import { cancel, later, next } from '@ember/runloop';
import { Promise } from 'rsvp';
import { A } from '@ember/array';
import * as Sentry from '@sentry/browser';
export const ANIMATION_DELAY = 150; // Keep in sync with $modal-blade-animate-duration

// Constants for understanding what caused the modal to close.
export const CLOSE_CLICK = 'close click';
export const CLOSE_ESC = 'close escape';
export const CLOSE_NAVIGATION = 'close navigation';
export const CLOSE_X = 'close x click';

/*

## Modal Service:

A central authority for presenting modals. Works in conjunction with the
{{modals/modal-manager}} component.

To use:

1. Create a component for your modal (most likely using `{{dialog-modal}}` in its template)
2. Call `open(component, options)` on the modal service.
3. The modal-manager will initialize your component and display it. Your component will
   receive two properties:

    * `close`: a function to close the modal.
    * `modalOptions`: the `options` argument in `open()`.

### Advanced usage:

#### Disable close on <esc> or clicking on the veil:

In your modal component's `didInsertElement`, call the `setCloseHandler` actions
with a function that returns false:

```js
didInsertElement(...args) {
  this._super(...args);
  this.get('setCloseHandler')(() => {
    return false;
  });
}
```

#### Lock and unlock the vertical height of the modal

Before and after making changes that might dramatically affect the height of the modal
and create a jarring user experience (e.g. hiding or showing a large element, filtering
a list), call `lockModalHeight`. Call `unlockModalHeight` to resume default behavior.

```js
startFiltering() {
  this.get('lockModalHeight')();
  // filter elements
},

doneFiltering() {
  // show all elements
  this.get('unlockModalHeight')();
}
```

*/
// eslint-disable-next-line ember/no-classic-classes
export default Service.extend(Evented, {
  flash: service(),
  scroll: service(),
  isOpen: false,
  stack: null,
  stringRegistry: null,
  init(...args) {
    this._super(...args);
    this.set('stack', A());
    const owner = getOwner(this);
    if (owner?.application?.modulePrefix !== 'dummy') {
      this.set('stringRegistry', new Set());
    }
  },
  _resolveComponent(componentName) {
    if (typeof componentName !== 'string') {
      // Not a string. Already resolved
      return componentName;
    }
    if (this.stringRegistry && !this.stringRegistry.has(componentName)) {
      assert(`Error missing entry in modal-registry: ${componentName}`);
      const error = new Error('Error missing entry in modal-registry');
      error.name = 'Error missing entry in modal-registry';
      Sentry.captureException(error, {
        level: Sentry.Severity.Warning,
        tags: {
          componentName
        }
      });
    }
    const componentClass = getOwner(this)?.resolveRegistration?.(`component:${componentName}`);
    if (componentClass) {
      return componentClass;
    } else {
      const error = new Error('Error missing class lookup in owner');
      error.name = 'Error missing class lookup in owner';
      Sentry.captureException(error, {
        level: Sentry.Severity.Error,
        tags: {
          componentName
        }
      });
      return componentName;
    }
  },
  /**
   * Opens a modal.
   *
   * @param component component class to use for the modal component
   * @param options passed to the component as `modalOptions`
   * @param metaOptions options which are not passed to the component
   * @returns {*}
   */
  open(component, options, {
    position
  } = {}) {
    const componentClass = this._resolveComponent(component);

    // allow consuming apps (*cough*dashboard*cough*) to listen for this event
    // and do any cleanup they might need to do
    this.trigger('onOpen');

    // Cancel any scheduled close animation
    if (this.delayedCloser) {
      cancel(this.delayedCloser);
      this.get('stack').popObject();
      this.delayedCloser = null;
    }

    // Disable scrolling while leaving the scroll bar visible
    this.get('scroll').disableWindowScrolling();
    return new Promise(resolver => {
      this.get('stack').pushObject({
        component: componentClass,
        options,
        resolver,
        closeHandler: null,
        metaOptions: {
          position
        }
      });
      next(() => this.set('isOpen', true));
    });
  },
  close(...args) {
    assert('Called close on modal service when it is not open', this.get('isOpen'));
    if (this.get('stack.length') === 1) {
      this.get('flash').clearModalMessage();
      this.get('flash').clearModalValidation();
      this.set('isOpen', false);
      this.delayedCloser = later(() => {
        this.cleanUpClosedModal(...args);
        this.delayedCloser = null;
      }, ANIMATION_DELAY);
    } else {
      this.cleanUpClosedModal(...args);
    }
  },
  willDestroy() {
    cancel(this.delayedCloser);
  },
  cleanUpClosedModal(...args) {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }

    // Restore scrolling and the original scroll position
    this.get('scroll').enableWindowScrolling();
    this.get('stack.lastObject.resolver')(...args);
    this.get('stack').popObject();
  },
  closeIfOpen(...args) {
    if (this.get('isOpen')) {
      this.close(...args);
    }
  },
  closeX() {
    this.maybeCloseOnEvent(CLOSE_X);
  },
  // If the close handler is missing (the default case), close the modal with no arguments
  // If the close handler exists and returns exactly true (somewhat common), close the modal with no arguments
  // If the close handler exists and returns something besides true or false, close the modal with the return value as an argument
  // (We don't want to pass `true` as an argument because many uses of open().then() expect an object or undefined.)
  maybeCloseOnEvent(closeReason) {
    if (this.get('stack.length') === 0) {
      // If there's no modal on the stack, there is nothing to close.
      return;
    }
    const {
      closeHandler
    } = this.get('stack.lastObject');
    if (typeof closeHandler !== 'function') {
      this.close();
      return;
    }
    const closeValue = closeHandler(closeReason);
    if (closeValue === true) {
      this.close();
    } else if (closeValue !== false) {
      this.close(closeValue);
    }
  }
});